"use client";

import * as stylex from "@stylexjs/stylex";
import { ArrowRight, SquarePen, StickyNote as StickyNoteGlyph } from "lucide-react";
import { type RefObject, useLayoutEffect, useRef, useState } from "react";
import { copy } from "@/copy";
import type { Breakpoint } from "@/domain/breakpoint";
import type { BoardNote } from "@/domain/notes";
import { type Clock, clockAt } from "@/domain/time";
import type { ZoneState } from "@/domain/zoneState";
import { noteStampText } from "@/features/notes";
import "@/theme/seasons.stylex";
import { season } from "@/theme/seasons.stylex";
import "@/theme/tokens.stylex";
import { colors, radii, space } from "@/theme/tokens.stylex";
import { Button } from "@/ui/Button";
import { Eyebrow } from "@/ui/Eyebrow";
import { Icon } from "@/ui/Icon";
import { IconButton } from "@/ui/IconButton";
import { NotePad } from "@/ui/NotePad";
import { press } from "@/ui/press";
import { Skeleton } from "@/ui/Skeleton";
import { SourceFailure } from "@/ui/SourceFailure";
import { StickyNote } from "@/ui/StickyNote";
import { useBoxSize } from "@/ui/useBoxSize";
import { Zone } from "@/ui/Zone";
import { fitNotes, type NotesRoom } from "../layout";
type TodayNotesProps = {
  state: ZoneState<BoardNote>;
  retryIn: number;
  onRetry: () => void;
  now: Date;
  clock: Clock;
  tz: string;
  breakpoint: Breakpoint;
  onOpenNote: (id: string) => void;
  onAddNote: () => void;
  onOpenNotes: () => void;
  xstyle?: stylex.StyleXStyles;
};
type HeaderActions = {
  add: boolean;
  all: boolean;
};
type ActionsProps = {
  actions: HeaderActions;
  narrow: boolean;
  onAddNote: () => void;
  onOpenNotes: () => void;
};
type NoteButtonProps = {
  note: BoardNote;
  hidden: boolean;
  time: string;
  onOpen: () => void;
};
type MoreRowProps = {
  notes: readonly BoardNote[];
  count: number;
  onOpenNotes: () => void;
};
type EmptyCardProps = {
  narrow: boolean;
  onAddNote: () => void;
};
type NoteTilesProps = {
  listRef: RefObject<HTMLDivElement | null>;
  fit: 1 | 2;
};
const NARROW_BOX = 440;
const MAX_SHOWN = 2;
const MAX_DOTS = 3;
const TILE_TILTS = [-2, 1.5] as const;
const FIT_ITEM = "[data-fit]";
const _temp = {
  k3aq6I: "xsqj5wx",
  "$$css": true
};
const styles = {
  root: {
    kVAEAm: "x1n2onr6",
    kWkggS: "xh540kj",
    kaIpWk: "xwe7p7i",
    kmVPX3: "x1vvitgl",
    kB7OPa: "x9f619",
    k1xSpc: "x78zum5",
    kXwgrk: "xdt5ytf",
    kOIVth: "x1hu4l6k",
    k7Eaqz: "xeuugli",
    $$css: true
  },
  iconButton: {
    kzqmXN: "x187nhsf",
    kZKoxP: "xn3w4p2",
    $$css: true
  },
  note: {
    k1xSpc: "x1lliihq",
    kzqmXN: "xh8yej3",
    k7Eaqz: "xeuugli",
    kMzoRj: "xc342km",
    kWkggS: "xjbqb8w",
    kmVPX3: "x1717udv",
    kVVagm: "xln7xf2",
    kMwMTN: "x1heor9g",
    k9WMMc: "x1yc453h",
    kkrTdU: "x1ypdohk",
    $$css: true
  },
  hidden: {
    k33iCy: "xlshs6z",
    $$css: true
  },
  tiles: {
    k1xSpc: "x78zum5",
    kXwgrk: "xdt5ytf",
    kOIVth: "x1c1vhfx",
    $$css: true
  },
  tile: {
    kAzted: "xbwy6ji",
    kaIpWk: "x1wz39yh",
    kWkggS: "x1hueysq",
    $$css: true
  },
  tilt: (degrees: number) => [_temp, {
    "--x-transform": `rotate(${degrees}deg)` != null ? `rotate(${degrees}deg)` : undefined
  }],
  more: {
    kzqmXN: "xh8yej3",
    kAzted: "x1wiwyrm",
    kMzoRj: "xc342km",
    kEafiO: "x178xt8z",
    kPef9Z: "x13fuv20",
    kLZC3w: "x1m23mi5",
    kaIpWk: "x2u8bby",
    kWkggS: "xjbqb8w",
    kLKAdn: "xr5lh1o",
    kGO01o: "x18d9i69",
    kg3NbH: "xnjsko4",
    k1xSpc: "x78zum5",
    kGNEyG: "x6s0dn4",
    kOIVth: "x1hu4l6k",
    kVVagm: "xln7xf2",
    kMwMTN: "x1heor9g",
    k9WMMc: "x1yc453h",
    kkrTdU: "x1ypdohk",
    $$css: true
  },
  empty: {
    kzQI83: "x1iyjqo2",
    kmuXW: "xs83m0k",
    kCS8Yb: "xdl72j9",
    kAzted: "xi3av73",
    kzqmXN: "xh8yej3",
    kMzoRj: "xvndefy",
    ksu8eU: "xbsl7fq",
    kVAM5u: "x1ky62hi",
    kaIpWk: "xwe7p7i",
    kWkggS: "xjbqb8w",
    kmVPX3: "x18ppelm",
    kB7OPa: "x9f619",
    k1xSpc: "x78zum5",
    kGNEyG: "x6s0dn4",
    kOIVth: "xf5y8le",
    kVVagm: "xln7xf2",
    kMwMTN: "x1heor9g",
    k9WMMc: "x1yc453h",
    kkrTdU: "x1ypdohk",
    $$css: true
  }
};
export function TodayNotes({
  state,
  retryIn,
  onRetry,
  now,
  clock,
  tz,
  breakpoint,
  onOpenNote,
  onAddNote,
  onOpenNotes,
  xstyle
}: TodayNotesProps) {
  const ref = useRef<HTMLElement>(null);
  const listRef = useRef<HTMLDivElement>(null);
  const narrow = (useBoxSize(ref)?.width ?? Number.POSITIVE_INFINITY) < NARROW_BOX;
  const total = state.kind === "ready" ? state.data.length : MAX_SHOWN;
  const room = useNotesRoom(listRef, total, fitKey(state));
  const fit = fitNotes(breakpoint, room);
  const actions = headerActions(state, fit);
  return <section ref={ref} {...stylex.props(styles.root, xstyle)}>
      <div className="x78zum5 x1qughib x6s0dn4 x152posx x1gg8mnh x1qj0mhr">
        <Eyebrow>{eyebrowFor(state, clock, tz)}</Eyebrow>
        <Actions actions={actions} narrow={narrow} onAddNote={onAddNote} onOpenNotes={onOpenNotes} />
      </div>
      <Zone state={state} loading={<NoteTiles listRef={listRef} fit={fit} />} empty={<EmptyCard narrow={narrow} onAddNote={onAddNote} />} failed={<SourceFailure icon={StickyNoteGlyph} title={copy.today.unavailable.notes} body={copy.zone.failedBody(retryIn)} retryLabel={copy.zone.retry} onRetry={onRetry} />}>
        {notes => <>
            <div ref={listRef} className="x1iyjqo2 xs83m0k x2lwn1j x78zum5 xdt5ytf x1c1vhfx xr5lh1o x96kwfj">
              {notes.slice(0, MAX_SHOWN).map((note, index) => <NoteButton key={note.id} note={note} hidden={index >= fit} time={noteStampText(note, now, clock, tz)} onOpen={() => onOpenNote(note.id)} />)}
            </div>
            {notes.length > fit && <MoreRow notes={notes.slice(fit, fit + MAX_DOTS)} count={notes.length - fit} onOpenNotes={onOpenNotes} />}
          </>}
      </Zone>
    </section>;
}
function Actions({
  actions,
  narrow,
  onAddNote,
  onOpenNotes
}: ActionsProps) {
  if (narrow) {
    return <div className="x78zum5 xt4j9g6">
        {actions.add && <IconButton icon={SquarePen} label={copy.today.notes.addNote} variant="canvas" onClick={onAddNote} xstyle={styles.iconButton} />}
        {actions.all && <IconButton icon={ArrowRight} label={copy.today.notes.allNotes} variant="canvas" onClick={onOpenNotes} xstyle={styles.iconButton} />}
      </div>;
  }
  return <div className="x78zum5 xt4j9g6">
      {actions.add && <Button variant="ghost" icon={SquarePen} onClick={onAddNote}>
          {copy.today.notes.addNote}
        </Button>}
      {actions.all && <Button variant="ghost" iconRight={ArrowRight} onClick={onOpenNotes}>
          {copy.today.notes.allNotes}
        </Button>}
    </div>;
}
function NoteButton({
  note,
  hidden,
  time,
  onOpen
}: NoteButtonProps) {
  return <button type="button" data-fit="" aria-label={copy.today.notes.noteName(note.author, note.text)} aria-hidden={hidden || undefined} inert={hidden} onClick={onOpen} {...stylex.props(styles.note, press.scale, hidden && styles.hidden)}>
      <StickyNote rotate={note.rotation} author={note.author} time={time} width="100%" minHeight={150}>
        <span className="x104kibb x1gzmk7r x1ua5tub xb3r6kr">{note.text}</span>
      </StickyNote>
    </button>;
}
function MoreRow({
  notes,
  count,
  onOpenNotes
}: MoreRowProps) {
  const label = copy.today.notes.more(count);
  return <div className="xr1yuqi xr5lh1o x96kwfj">
      <button type="button" aria-label={copy.today.notes.moreName(label)} onClick={onOpenNotes} {...stylex.props(styles.more, press.scale)}>
        <span aria-hidden="true" className="x78zum5 x2lah0s">
          {notes.map(note => <span key={note.id} className="xgd8bvy x1fgtraw xqhrb7l xjmit6t x1qfufaz x1pwwqoy x1hueysq" />)}
        </span>
        <span className="x1iyjqo2 xeuugli x19d36u7 x1cf7cre">{label}</span>
        <Icon icon={ArrowRight} size={20} />
      </button>
    </div>;
}
function EmptyCard({
  narrow,
  onAddNote
}: EmptyCardProps) {
  return <button type="button" aria-label={copy.notes.addFirst} onClick={onAddNote} {...stylex.props(styles.empty, press.scale)}>
      {!narrow && <NotePad />}
      <span className="x1iyjqo2 xeuugli x78zum5 xdt5ytf x1cy8zhl x1af02g3">
        <span className="x78zum5 xdt5ytf x1jnr06f">
          <span className="x1i1m3gp x1v94iar x1tup8yo x1u7k74">{copy.today.notes.emptyTitle}</span>
          <span className="x1j61zf2 x15i68ds x37zpob x1fzhlzt">{copy.today.notes.emptyBody}</span>
        </span>
        <span className="x3nfvp2 x6s0dn4 x152posx xn3w4p2 xizoatq x1xnnf8n xqhrb7l xh540kj x98nf4m x19d36u7 x1cf7cre">
          <Icon icon={SquarePen} size={18} />
          {copy.today.notes.addNote}
        </span>
      </span>
    </button>;
}
function NoteTiles({
  listRef,
  fit
}: NoteTilesProps) {
  return <div ref={listRef} className="x1iyjqo2 xs83m0k x2lwn1j x78zum5 xdt5ytf x1c1vhfx xr5lh1o x96kwfj">
      <Skeleton label={copy.today.notes.loading} xstyle={styles.tiles}>
        {TILE_TILTS.map((tilt, index) => <div key={tilt} data-fit="" aria-hidden="true" {...stylex.props(styles.tile, styles.tilt(tilt), index >= fit && styles.hidden)} />)}
      </Skeleton>
    </div>;
}
function headerActions(state: ZoneState<BoardNote>, fit: 1 | 2): HeaderActions {
  if (state.kind === "loading") return {
    add: true,
    all: false
  };
  if (state.kind === "empty") return {
    add: false,
    all: false
  };
  if (state.kind === "failed") return {
    add: true,
    all: true
  };
  return {
    add: true,
    all: state.data.length <= fit
  };
}
function eyebrowFor(state: ZoneState<BoardNote>, clock: Clock, tz: string): string {
  if (state.kind !== "ready") return copy.today.notes.eyebrow;
  const count = copy.today.notes.count(state.data.length);
  if (state.updatedAt === undefined) return count;
  return copy.zone.updated(count, clockAt(state.updatedAt, clock, tz));
}
function fitKey(state: ZoneState<BoardNote>): string {
  if (state.kind !== "ready") return state.kind;
  return state.data.slice(0, MAX_SHOWN).map(note => `${note.id}:${note.text}`).join("|");
}
function useNotesRoom(listRef: RefObject<HTMLDivElement | null>, total: number, key: string): NotesRoom | null {
  const [room, setRoom] = useState<NotesRoom | null>(null);

  // biome-ignore lint/correctness/useExhaustiveDependencies: key re-measures when the shown notes change
  useLayoutEffect(() => {
    const list = listRef.current;
    if (list === null) return;
    const measure = () => setRoom(previous => sameRoom(previous, measuredRoom(list, total)));
    measure();
    const observer = new ResizeObserver(measure);
    observer.observe(list);
    for (const item of list.querySelectorAll(FIT_ITEM)) observer.observe(item);
    return () => observer.disconnect();
  }, [listRef, total, key]);
  return room;
}
function measuredRoom(list: HTMLElement, total: number): NotesRoom | null {
  const items = list.querySelectorAll<HTMLElement>(FIT_ITEM);
  const last = items[MAX_SHOWN - 1];
  if (last === undefined) return null;
  const need = last.offsetTop + last.offsetHeight - list.offsetTop;
  return {
    room: list.clientHeight + moreRowRoom(list, total),
    need
  };
}
function moreRowRoom(list: HTMLElement, total: number): number {
  const more = list.nextElementSibling;
  if (total !== MAX_SHOWN || !(more instanceof HTMLElement)) return 0;
  return more.offsetTop + more.offsetHeight - (list.offsetTop + list.offsetHeight);
}
function sameRoom(previous: NotesRoom | null, next: NotesRoom | null): NotesRoom | null {
  if (previous === null || next === null) return next;
  return previous.room === next.room && previous.need === next.need ? previous : next;
}